import { createAsyncThunk } from "@reduxjs/toolkit";
import { GetChats } from "../../domain/usecases/getChat";
import {
	GetMessages,
	GetMessagesParams,
} from "../../domain/usecases/getMessages";
import { AddMessage, AddMessageParams } from "../../domain/usecases/addMessage";
import {
	UpdateMessageRead,
	UpdateMessageReadParams,
} from "../../domain/usecases/updateMessageRead";
import { NewMessageResponse } from "../../domain/entities/onNewMessageSocket";
import { UpdatedMessageResponse } from "../../domain/entities/onUpdatedMessageSocket";
import {
	messageUpdatedRead,
	newMessageReceived,
	tempMessageReceived,
	updatedChatStatusReceived,
} from "./ChatSlice";
import { ConnectSocketChat } from "../../domain/usecases/connectSocket";
import { ChatDisconnectSocket } from "../../domain/usecases/disconnectSocket";
import { OnNewMessage } from "../../domain/usecases/onNewMessage";
import { OnTempMessageSeen } from "../../domain/usecases/onTempMessageSeen";
import { OnUpdateMessage } from "../../domain/usecases/onUpdateMessage";
import { EmitTempMessage } from "../../domain/usecases/emitTempMessage";
import { ChatMessage } from "../../domain/entities/getMessages";
import { OnUpdatedChatStatus } from "../../domain/usecases/onUpdatedChatStatus";
import { UpdatedChatStatusSocket } from "../../domain/entities/UpdatedChatStatusSocket";
import { UpdateChatStatus, UpdateChatStatusParams } from "../../domain/usecases/updateChatStatus";
import { ClearChat } from "../../domain/usecases/clearChat";
import { deleteNewMatch } from "../../../match/presentation/store/MatchSlice";
import { UploadFile, UploadFileParams } from "../../domain/usecases/uploadFile";
import { showIncomingChatBrowserNotification } from "../../../../core/utilities/incomingChatBrowserNotification";

export const onGetChats = createAsyncThunk(
	"chat/chats",
	async (_, { extra }) => {
		const { getChats } = extra as { getChats: GetChats };
		const result = await getChats.execute();
		return result;
	}
);

export const onGetMessages = createAsyncThunk(
	"chat/messages",
	async (params: GetMessagesParams, { extra }) => {
		const { getMessages } = extra as {
			getMessages: GetMessages;
		};
		const result = await getMessages.execute(params);
		return result;
	}
);

export const onConnectSocketChat = createAsyncThunk(
	"chat/connect-socket",
	async (_, { extra }) => {
		const { connectSocketChat } = extra as {
			connectSocketChat: ConnectSocketChat;
		};
		await connectSocketChat.execute();
		return;
	}
);

export const onDisconnectSocketChat = createAsyncThunk(
	"chat/disconnect-socket",
	async (_, { extra }) => {
		const { disconnectSocket } = extra as {
			disconnectSocket: ChatDisconnectSocket;
		};
		await disconnectSocket.execute();
		return;
	}
);

export const onAddMessage = createAsyncThunk(
	"chat/add-message",
	async (params: AddMessageParams, { extra }) => {
		const { addMessage } = extra as {
			addMessage: AddMessage;
		};
		await addMessage.execute(params);
		return;
	}
);

export const onUpdateMessageRead = createAsyncThunk(
	"chat/update-message-read",
	async (params: UpdateMessageReadParams, { extra }) => {
		const { updateMessageRead } = extra as {
			updateMessageRead: UpdateMessageRead;
		};
		await updateMessageRead.execute(params);
		return;
	}
);

export const onListenNewMessages = createAsyncThunk(
	"chat/start-listening-for-new-messages",
	async (_, { extra, dispatch }) => {
		const { onNewMessage } = extra as {
			onNewMessage: OnNewMessage;
		};
		onNewMessage.execute((message: NewMessageResponse) => {
			dispatch(newMessageReceived(message));
			showIncomingChatBrowserNotification(message);
			dispatch(deleteNewMatch(message.chat.user_id));
		});
	}
);

export const onListenUpdatedMessages = createAsyncThunk(
	"chat/start-listening-for-updated-messages",
	async (_, { extra, dispatch }) => {
		const { onUpdateMessage } = extra as {
			onUpdateMessage: OnUpdateMessage;
		};
		onUpdateMessage.execute((messages: UpdatedMessageResponse[]) => {
			dispatch(messageUpdatedRead(messages));
		});
	}
);

export const sendEmitTempMessages = createAsyncThunk(
	"chat/send-emit-temp-messages",
	async (message_id: string, { extra }) => {
		const { emitTempMessage } = extra as {
			emitTempMessage: EmitTempMessage;
		};
		await emitTempMessage.execute(message_id);
		return;
	}
);

export const onListenTempMessages = createAsyncThunk(
	"chat/start-listening-for-temp-messages",
	async (_, { extra, dispatch }) => {
		const { onTempMessageSeen } = extra as {
			onTempMessageSeen: OnTempMessageSeen;
		};
		onTempMessageSeen.execute((data: ChatMessage) => {
			dispatch(tempMessageReceived(data));
		});
	}
);

export const onListenUpdatedChatStatus = createAsyncThunk(
	"chat/start-listening-for-updated-chat-status",
	async (_, { extra, dispatch }) => {
		const { onUpdatedChatStatus } = extra as {
			onUpdatedChatStatus: OnUpdatedChatStatus;
		};
		onUpdatedChatStatus.execute((data: UpdatedChatStatusSocket) => {
			dispatch(updatedChatStatusReceived(data));
		});
	}
);

export const sendUpdateChatStatus = createAsyncThunk(
	"chat/send-update-chat-status",
	async (params: UpdateChatStatusParams, { extra }) => {
		const { updateChatStatus } = extra as {
			updateChatStatus: UpdateChatStatus;
		};
		await updateChatStatus.execute(params);
		return;
	}
);

export const clearChat = createAsyncThunk(
	"chat/clear-chat",
	async (chat_id: string, { extra }) => {
		const { clearChat } = extra as { clearChat: ClearChat }; 
		await clearChat.execute(chat_id); 
		return;
	}
);

export const uploadFile = createAsyncThunk(
	"chat/upload-message",
	async (params: UploadFileParams, { extra }) => {
		const { uploadFile } = extra as { uploadFile: UploadFile };
		await uploadFile.execute(params);
		return;
	}
);
